/**
 * IXVerify v2 — inline subject sheet (ixv2 WP-3.2; plan §3.2 step 3).
 *
 * Two modes on one sheet:
 *   - 'add' (default): the extracted subject isn't on the roster — subject
 *     number prefilled, DOB keyed ONCE, `source='manual'` server-side +
 *     flagged for admin confirmation (§3.4).
 *   - 'dob' (2026-09-04): the subject IS on the roster but carries no DOB
 *     (CRIO had none in the patient profile; the epoch-zero placeholder was
 *     cleared). The number is fixed, only the DOB is keyed; it lands via
 *     PATCH /subjects/:id — audited, and it clears any prior admin
 *     confirmation so the chip asks for a fresh one.
 * The DOB is typed as 01Dec2020 (lib/dob — the one format, 2026-09-29) and
 * sent as ISO; the hint under the field reads the value back so a slip is
 * seen before it is saved.
 * DOB is PHI (R3) — it goes straight to the API and is never logged or
 * echoed anywhere else.
 */
import { useState } from 'react';
import { CalendarDays, Loader2, UserPlus, X } from 'lucide-react';
import { DOB_FORMAT_EXAMPLE, formatDob, parseDob } from '../../lib/dob';

const fieldInputCls =
  'w-full px-2.5 py-1.5 rounded-lg border border-gray-300 bg-white text-sm text-gray-900 placeholder-gray-400 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500 transition-colors duration-150 motion-reduce:transition-none dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100';

export type SubjectSheetMode = 'add' | 'dob';

export default function SubjectSheet({
  mode = 'add',
  prefillSubjectNumber,
  saving,
  error,
  onClose,
  onSubmit,
}: {
  mode?: SubjectSheetMode;
  prefillSubjectNumber: string;
  saving: boolean;
  error: string | null;
  onClose: () => void;
  onSubmit: (fields: { subject_number: string; dob: string; initials?: string }) => void;
}) {
  const dobOnly = mode === 'dob';
  const [subjectNumber, setSubjectNumber] = useState(prefillSubjectNumber);
  const [dob, setDob] = useState('');
  const [initials, setInitials] = useState('');

  const dobIso = parseDob(dob);
  const dobInvalid = dob.trim() !== '' && !dobIso;
  const ready = subjectNumber.trim() !== '' && dobIso != null;
  const title = dobOnly ? 'Enter date of birth' : 'Add subject';
  const Icon = dobOnly ? CalendarDays : UserPlus;

  return (
    <div
      className="fixed inset-0 z-50 flex items-end justify-center bg-black/50 p-4 backdrop-blur-sm sm:items-center"
      style={{ paddingBottom: 'calc(env(safe-area-inset-bottom) + 1rem)' }}
      role="dialog"
      aria-modal="true"
      aria-label={title}
    >
      <div className="w-full max-w-sm rounded-xl bg-white p-5 shadow-xl dark:bg-gray-800" data-testid="subject-sheet" data-mode={mode}>
        <div className="flex items-center justify-between">
          <h3 className="flex items-center gap-2 text-lg font-semibold text-gray-900 dark:text-gray-100">
            <Icon className="h-5 w-5 text-primary-600 dark:text-primary-400" aria-hidden="true" /> {title}
          </h3>
          <button
            type="button"
            aria-label="Close"
            onClick={onClose}
            className="inline-flex h-11 w-11 items-center justify-center rounded-lg text-gray-500 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:text-gray-400 dark:hover:bg-gray-700 sm:h-8 sm:w-8"
          >
            <X className="h-5 w-5" aria-hidden="true" />
          </button>
        </div>
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
          {dobOnly
            ? 'Copy it from the source document, not from memory. It is saved to the roster and an admin will confirm it later.'
            : 'Added to the roster as a manual entry — an admin will confirm the DOB later.'}
        </p>

        <div className="mt-3 space-y-3">
          {dobOnly ? (
            <p className="text-sm text-gray-700 dark:text-gray-300">
              Subject <span className="font-medium tabular-nums text-gray-900 dark:text-gray-100">{subjectNumber}</span>
            </p>
          ) : (
            <label className="block">
              <span className="mb-1 block text-xs font-medium text-gray-700 dark:text-gray-300">Subject number</span>
              <input
                aria-label="Subject number"
                className={fieldInputCls}
                value={subjectNumber}
                onChange={(e) => setSubjectNumber(e.target.value)}
              />
            </label>
          )}
          <label className="block">
            <span className="mb-1 block text-xs font-medium text-gray-700 dark:text-gray-300">Date of birth</span>
            <input
              aria-label="Date of birth"
              aria-describedby="ixv-subject-sheet-dob-hint"
              aria-invalid={dobInvalid || undefined}
              type="text"
              autoComplete="off"
              autoCorrect="off"
              spellCheck={false}
              placeholder={DOB_FORMAT_EXAMPLE}
              className={fieldInputCls}
              value={dob}
              onChange={(e) => setDob(e.target.value)}
            />
            <span
              id="ixv-subject-sheet-dob-hint"
              className={`mt-1 block text-xs ${dobInvalid ? 'text-amber-700 dark:text-amber-400' : 'text-gray-600 dark:text-gray-400'}`}
              data-testid="dob-hint"
            >
              {dobIso ? `Reads as ${formatDob(dobIso)}` : dobInvalid ? `Type it as ${DOB_FORMAT_EXAMPLE}` : `Like ${DOB_FORMAT_EXAMPLE}`}
            </span>
          </label>
          {!dobOnly && (
            <label className="block">
              <span className="mb-1 block text-xs font-medium text-gray-700 dark:text-gray-300">Initials (optional)</span>
              <input
                aria-label="Initials"
                className={fieldInputCls}
                maxLength={4}
                value={initials}
                onChange={(e) => setInitials(e.target.value.toUpperCase())}
              />
            </label>
          )}
        </div>

        {error && (
          <p className="mt-3 rounded-lg border border-red-200 bg-red-50 p-2.5 text-sm text-red-800 dark:border-red-800 dark:bg-red-900/20 dark:text-red-300" role="alert">
            {error}
          </p>
        )}

        <div className="mt-4 flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="inline-flex min-h-[44px] items-center rounded-lg border border-gray-300 px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700 sm:min-h-0"
          >
            Cancel
          </button>
          <button
            type="button"
            disabled={!ready || saving}
            onClick={() => dobIso && onSubmit({ subject_number: subjectNumber.trim(), dob: dobIso, ...(!dobOnly && initials.trim() ? { initials: initials.trim() } : {}) })}
            className="inline-flex min-h-[44px] items-center gap-1.5 rounded-lg bg-primary-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:cursor-not-allowed disabled:opacity-50 sm:min-h-0"
            data-testid="subject-sheet-save"
          >
            {saving && <Loader2 className="h-4 w-4 animate-spin motion-reduce:animate-none" aria-hidden="true" />} {dobOnly ? 'Save date of birth' : 'Add subject'}
          </button>
        </div>
      </div>
    </div>
  );
}
